/**
 * Utility functions for AG Grid table filtering, including date filters and URL parameter handling.
 */

export interface FilterConfig {
    param: string;
    column: string;
    type: 'dropdown' | 'date';
}

/**
 * Parses date filter values from URL parameters.
 * Expected format: "operator|from|to" or "operator|date"
 * Examples:
 * - "between|2024-01-01|2024-12-31"
 * - "after|2024-01-01"
 * - "before|2024-12-31"
 */
export const parseUrlDateFilter = (rawValues: string) => {
    if (!rawValues) return null;

    const [operator, from, to] = rawValues.split('|');
    const toDate = (value?: string) => (value ? new Date(value) : undefined);

    try {
        return operator === 'between'
            ? { operator, dateRange: { from: toDate(from), to: toDate(to) } }
            : { operator, date: toDate(from) };
    } catch (e) {
        console.warn('Failed to parse date filter from URL:', e);
        return null;
    }
};

/**
 * Creates a date filter configuration for AG Grid columns.
 * This function returns a filter config object that includes both the DateFilter component
 * and the handler methods required by AG Grid.
 * 
 * @param filterParam - The URL parameter name for this filter (e.g., 'dueDate')
 * @param DateFilterComponent - The DateFilter component from @lendica/components
 */
export const createDateFilterConfig = (filterParam: string, DateFilterComponent: any) => ({
    component: (props: any) => (
        DateFilterComponent({
            filterParam,
            ...props
        })
    ),
    handler: () => ({
        doesFilterPass: (params: any) => DateFilterComponent.doesFilterPass(params),
        refresh: (params: any) => DateFilterComponent.refresh(params, filterParam)
    })
});

/**
 * Creates a simple dropdown filter configuration for AG Grid columns.
 * Use this for basic dropdowns without search functionality.
 * 
 * @param filterParam - The URL parameter name for this filter (e.g., 'status')
 * @param options - Array of options to display in the dropdown
 * @param DropdownFilterComponent - The DropdownFilter component from @lendica/components
 */
export const createDropdownFilterConfig = (
    filterParam: string,
    options: string[],
    DropdownFilterComponent: any
) => ({
    component: (props: any) => (
        DropdownFilterComponent({
            options,
            filterParam,
            ...props
        })
    ),
    handler: () => ({
        doesFilterPass: (params: any) => DropdownFilterComponent.doesFilterPass(params),
        refresh: (params: any) => DropdownFilterComponent.refresh(params, filterParam)
    })
});

interface AdvancedDropdownFilterOptions {
    filterParam: string;
    options: string[];
    enableSearch?: boolean;
    blankOption?: string;
    nullValueFallback?: string;
}

/**
 * Creates an advanced dropdown filter configuration with search and blank option support.
 * Use this for dropdowns that need search functionality and/or blank value handling.
 * 
 * @param config - Configuration object for the advanced dropdown filter
 * @param DropdownFilterComponent - The DropdownFilter component from @lendica/components
 */
export const createAdvancedDropdownFilterConfig = (
    config: AdvancedDropdownFilterOptions,
    DropdownFilterComponent: any
) => {
    const { filterParam, options, enableSearch, blankOption, nullValueFallback } = config;

    return {
        component: (props: any) => (
            DropdownFilterComponent({
                options,
                filterParam,
                enableSearch: enableSearch ?? false,
                ...props
            })
        ),
        handler: () => ({
            doesFilterPass: (params: any) => {
                const filterParams = blankOption || nullValueFallback
                    ? { ...params, blankOption, nullValueFallback }
                    : params;
                return DropdownFilterComponent.doesFilterPass(filterParams);
            },
            refresh: (params: any) => DropdownFilterComponent.refresh(params, filterParam)
        })
    };
};

/**
 * Applies filters from URL parameters to an AG Grid instance.
 * This function should be called in the onGridReady callback.
 * 
 * @param gridApi - The AG Grid API instance
 * @param filterConfigs - Array of filter configurations specifying which URL params map to which columns
 */
export const applyFiltersFromUrl = (
    gridApi: any,
    filterConfigs: FilterConfig[]
) => {
    const paramsUrl = new URLSearchParams(window.location.search);
    let hasFiltersToApply = false;

    filterConfigs.forEach(({ param, column, type }) => {
        const rawValues = paramsUrl.get(param);
        if (rawValues && rawValues.length > 0) {
            if (type === 'date') {
                const dateFilter = parseUrlDateFilter(rawValues);
                if (dateFilter) {
                    gridApi.setColumnFilterModel(column, dateFilter);
                    hasFiltersToApply = true;
                }
            } else {
                // Handle dropdown filters (comma-separated values)
                const values = rawValues.split(',');
                gridApi.setColumnFilterModel(column, values);
                hasFiltersToApply = true;
            }
        }
    });

    // Apply all filters if any were set
    if (hasFiltersToApply) {
        gridApi.onFilterChanged();
    }
};

